import React, { useState } from 'react';
import { FileTextIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function Reports() {
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const reportCards = [{
    id: 'permits',
    title: 'Permit Issuance Summary',
    description: 'Overview of permits issued by type and period'
  }, {
    id: 'trips',
    title: 'Trip Activity Report',
    description: 'Forest entry and exit tracking statistics'
  }, {
    id: 'beneficiaries',
    title: 'Beneficiary Statistics',
    description: 'Demographics and registration trends'
  }, {
    id: 'compliance',
    title: 'Compliance & Cases Report',
    description: 'Offense tracking and resolution status'
  }, {
    id: 'revenue',
    title: 'Revenue Collection Report',
    description: 'Fees and fines collected'
  }, {
    id: 'performance',
    title: 'Station Performance Report',
    description: 'Performance metrics by station'
  }];
  const sampleData = [{
    month: 'Oct',
    value: 520
  }, {
    month: 'Nov',
    value: 680
  }, {
    month: 'Dec',
    value: 590
  }, {
    month: 'Jan',
    value: 720
  }, {
    month: 'Feb',
    value: 650
  }, {
    month: 'Mar',
    value: 780
  }];
  return <div className="space-y-6" data-id="element-2015">
      <div data-id="element-2016">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2017">
          Reports & Analytics
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2018">
          Generate and export system reports
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2019">
        <div className="flex items-center gap-4" data-id="element-2020">
          <div className="flex-1" data-id="element-2021">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2022">
              Date Range
            </label>
            <div className="flex gap-2" data-id="element-2023">
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2024" />
              <span className="flex items-center text-gray-600" data-id="element-2025">to</span>
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2026" />
            </div>
          </div>
          <div data-id="element-2027">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2028">
              Report Type
            </label>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2029">
              <option value="" data-id="element-2030">All Types</option>
              <option value="permits" data-id="element-2031">Permits</option>
              <option value="trips" data-id="element-2032">Trips</option>
              <option value="compliance" data-id="element-2033">Compliance</option>
            </select>
          </div>
          <div data-id="element-2034">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2035">
              Station/Range
            </label>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2036">
              <option value="" data-id="element-2037">All Stations</option>
              <option value="chandpai" data-id="element-2038">Chandpai Range</option>
              <option value="katka" data-id="element-2039">Katka Station</option>
              <option value="dhangmari" data-id="element-2040">Dhangmari Station</option>
            </select>
          </div>
          <div className="self-end" data-id="element-2041">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2042">
              <DownloadIcon size={16} data-id="element-2043" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2044">
        {reportCards.map(report => <button key={report.id} onClick={() => setSelectedReport(report.id)} className={`bg-white rounded-lg border-2 p-6 text-left transition-all hover:shadow-lg ${selectedReport === report.id ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200'}`} data-id="element-2045">
            <FileTextIcon className={`mb-3 ${selectedReport === report.id ? 'text-[#1B5E20]' : 'text-gray-400'}`} size={32} data-id="element-2046" />
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2047">{report.title}</h3>
            <p className="text-sm text-gray-600" data-id="element-2048">{report.description}</p>
          </button>)}
      </div>

      {/* Report View */}
      {selectedReport && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2049">
          <div className="flex items-center justify-between mb-6" data-id="element-2050">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-2051">
              {reportCards.find(r => r.id === selectedReport)?.title}
            </h3>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2052">
              <DownloadIcon size={16} data-id="element-2053" />
              Download PDF
            </button>
          </div>

          {/* Sample Chart */}
          <div className="mb-6" data-id="element-2054">
            <ResponsiveContainer width="100%" height={300} data-id="element-2055">
              <BarChart data={sampleData} data-id="element-2056">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2057" />
                <XAxis dataKey="month" data-id="element-2058" />
                <YAxis data-id="element-2059" />
                <Tooltip data-id="element-2060" />
                <Bar dataKey="value" fill="#1B5E20" data-id="element-2061" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sample Table */}
          <div className="overflow-x-auto" data-id="element-2062">
            <table className="w-full" data-id="element-2063">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2064">
                <tr data-id="element-2065">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2066">
                    Period
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2067">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2068">
                    Approved
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2069">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2070">
                    Rejected
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2071">
                {sampleData.map((row, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-2072">
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2073">
                      {row.month} 2024
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2074">
                      {row.value}
                    </td>
                    <td className="px-4 py-3 text-sm text-green-600" data-id="element-2075">
                      {Math.floor(row.value * 0.85)}
                    </td>
                    <td className="px-4 py-3 text-sm text-yellow-600" data-id="element-2076">
                      {Math.floor(row.value * 0.1)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600" data-id="element-2077">
                      {Math.floor(row.value * 0.05)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}